import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import Oncology1 from "../assets/images/Oncology/Oncology1.jpg";
import Oncology2 from "../assets/images/Oncology/Oncology2.webp";
import Oncology3 from "../assets/images/Oncology/Oncology3.jpg";
import Oncology4 from "../assets/images/Oncology/Oncology4.jpg";
import Oncology5 from "../assets/images/Oncology/Oncology5.webp";
import Oncology6 from "../assets/images/Oncology/Oncology6.jpeg";
import Oncology7 from "../assets/images/Oncology/Oncology7.jpg";
import Oncology8 from "../assets/images/Oncology/Oncology8.webp";
import Oncology9 from "../assets/images/Oncology/Oncology9.jpg";
import SEO from "../components/SEO";

/**
 * Oncology Unit Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Treatment Type), 2D/3D image toggle,
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const oncologyUnits = [
  {
    name: "Chemotherapy Day-Care Unit",
    zone: "Level 1",
    size: "60 sq.m / 10 chairs",
    spec: "Chemotherapy",
    tag: "Day-Care",
    note: "Reclining infusion chairs, individual TV/charging points, dedicated pharmacy pass-through.",
    image: Oncology1, // 👈 add your image path/URL here
  },
  {
    name: "Radiation Therapy (LINAC) Bunker",
    zone: "Basement",
    size: "80 sq.m",
    spec: "Radiation Therapy",
    tag: "LINAC",
    note: "Reinforced concrete shielding walls, maze entry, dedicated control console room.",
    image: Oncology2, // 👈 add your image path/URL here
  },
  {
    name: "Brachytherapy Treatment Suite",
    zone: "Basement",
    size: "35 sq.m",
    spec: "Brachytherapy",
    tag: "Brachytherapy",
    note: "Radiation-shielded procedure room, recovery bay adjacency, lead-lined storage safe.",
    image: Oncology3, // 👈 add your image path/URL here
  },
  {
    name: "Oncology In-Patient Ward",
    zone: "Level 2",
    size: "45 sq.m / 4 beds",
    spec: "In-Patient Care",
    tag: "In-Patient",
    note: "Infection-control finishes, private bathrooms, calming non-clinical interior palette.",
    image: Oncology4, // 👈 add your image path/URL here
  },
  {
    name: "Bone Marrow Transplant (BMT) Unit",
    zone: "Level 3",
    size: "30 sq.m",
    spec: "BMT / HEPA Isolation",
    tag: "BMT",
    note: "Positive-pressure HEPA rooms, strict anteroom gowning, dedicated sterile supply chain.",
    image: Oncology5, // 👈 add your image path/URL here
  },
  {
    name: "Oncology Outpatient Consultation Suite",
    zone: "Ground Floor",
    size: "40 sq.m",
    spec: "OPD Consultation",
    tag: "OPD",
    note: "Private counseling rooms, calm waiting lounge, integrated tumor-board conference room.",
    image: Oncology6, // 👈 add your image path/URL here
  },
  {
    name: "Palliative & Supportive Care Room",
    zone: "Level 2",
    size: "24 sq.m",
    spec: "Palliative Care",
    tag: "Palliative",
    note: "Home-like furnishings, family lounge seating, dedicated quiet/reflection corner.",
    image: Oncology7, // 👈 add your image path/URL here
  },
  {
    name: "Oncology Diagnostic & Biopsy Suite",
    zone: "Ground Floor",
    size: "28 sq.m",
    spec: "Biopsy / PET-CT",
    tag: "Diagnostics",
    note: "PET-CT shielded bay, biopsy procedure room, dedicated specimen-handling pass box.",
    image: Oncology8, // 👈 add your image path/URL here
  },
  {
    name: "Comprehensive Cancer Care Centre",
    zone: "Multi-Level",
    size: "300 sq.m",
    spec: "Full-Service",
    tag: "Cancer Centre",
    note: "Integrated chemo, radiation, diagnostics and counseling under one coordinated patient pathway.",
    image: Oncology9, // 👈 add your image path/URL here
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function OncologyUnitCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: "transparent", color: tokens.teal,
              border: `1.4px solid ${tokens.teal}`, borderRadius: 3, cursor: "pointer",
            }}
          >
            View Layout
          </button>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OncologyUnitDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .onco-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .onco-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .onco-grid-3x3 { grid-template-columns: 1fr; } }
        .onco-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .onco-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .onco-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="onco-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {oncologyUnits.map((item, i) => (
            <OncologyUnitCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="onco-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="onco-modal-inner">
            <button
              className="onco-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}